import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFormattedPrivileges, useLogin, useUserPrivilegeKeyPermissionsMap } from "../../hooks/use_backend";
import { Edit, Lock } from "@mui/icons-material";
import UserPopover from "../user_management/add_user_popover";
import { UserActions } from "../user_management/user_actions_enum";
import { isComponentDisabledForUpdates } from "../login/login_utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getTechnicalSupport, setTechnicalSupport } from "../../scripts/api_v2/user_client";
import useBackend from "../../hooks/use_backend";
import Snackbar from "../../scripts/snackbar";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";

export default function ProfilePage({ required_privilege }) {
  const login = useLogin();
  const backend = useBackend();
  const queryClient = useQueryClient();
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const privileges = useFormattedPrivileges();
  const isMobile = window.innerWidth < 600;

  const { data: technicalSupportEnabled = false, isLoading: isTechnicalSupportLoading } = useQuery({
    queryKey: [REACT_QUERY_KEYS.USER_TECHNICAL_SUPPORT, backend.fetch],
    queryFn: () => getTechnicalSupport(backend.fetch),
    staleTime: 0,
    refetchOnMount: true,
  });

  const [lastUserDialogOpen, setLastUserDialogOpen] = useState(false);

  const [popoverData, setPopoverData] = useState({
    open: false,
    anchorEl: null,
    userRow: {},
    actionType: "",
    partnerCompaniesData: null,
    companiesError: false,
    userRolesData: null,
    rolesError: false,
  });

  const listItemStyle = {
    marginTop: -8,
    marginBottom: -8,
  };

  const listItemStyleFeature = {
    paddingLeft: "2em",
    marginTop: -8,
    marginBottom: -8,
  };

  const listItemStyleComponents = {
    paddingLeft: "4em",
    margin: 0,
  };

  const containerStyle = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const componentStyle = {
    flex: "1 1 50%",
    padding: "0 8px",
  };

  const typographyBold = {
    fontWeight: "bold",
  };

  const PrivilegesList = ({ privileges }) => (
    <List dense>
      {Array.from(privileges.entries()).map(([feature, items], featureIndex) => (
        <React.Fragment key={feature}>
          <Divider />
          <ListItem style={listItemStyleFeature}>
            <ListItemText
              primary={
                <Typography variant="body1" style={typographyBold}>
                  {feature}
                </Typography>
              }
            />
          </ListItem>
          <Divider />
          <ListItem style={listItemStyleComponents}>
            <div style={containerStyle}>
              {items.map((item, index) => (
                <div key={index} style={componentStyle}>
                  <Typography variant="body2" style={{ fontWeight: "bold" }}>
                    {item.component}
                  </Typography>
                  <Typography variant="body2">{item.permissions.join(", ")}</Typography>
                </div>
              ))}
            </div>
          </ListItem>
          {featureIndex < privileges.size - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  );

  const handlePopoverOpen = (event, row, type) => {
    setPopoverData({
      ...popoverData,
      open: true,
      anchorEl: event.currentTarget,
      userRow: row,
      actionType: type,
      partnerCompaniesData: null,
      companiesError: false,
      userRolesData: null,
      rolesError: false,
    });
  };

  const handlePopoverClose = () => {
    setPopoverData({
      ...popoverData,
      open: false,
      anchorEl: null,
      userRow: {},
      actionType: "",
      partnerCompaniesData: null,
      companiesError: false,
      userRolesData: null,
      rolesError: false,
    });
  };

  const { mutate: updateTechnicalSupport, isPending: isTechnicalSupportPending } = useMutation({
    mutationFn: async (active) => await setTechnicalSupport(backend.fetch, active),
    onMutate: async (newValue) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [REACT_QUERY_KEYS.USER_TECHNICAL_SUPPORT, backend.fetch] });

      // Snapshot the previous value
      const previousValue = queryClient.getQueryData([REACT_QUERY_KEYS.USER_TECHNICAL_SUPPORT, backend.fetch]);

      // Optimistically update to the new value
      queryClient.setQueryData([REACT_QUERY_KEYS.USER_TECHNICAL_SUPPORT, backend.fetch], newValue);

      // Return context with the previous value
      return { previousValue };
    },
    onSuccess: () => {
      Snackbar.success("Email notification preference updated");
    },
    onError: (err, newValue, context) => {
      // Rollback to the previous value on error
      queryClient.setQueryData([REACT_QUERY_KEYS.USER_TECHNICAL_SUPPORT, backend.fetch], context.previousValue);

      // Check if this is the "last user" validation error
      if (err.message && err.message.includes("Cannot disable technical support")) {
        setLastUserDialogOpen(true);
      } else {
        Snackbar.error(err.message || "Failed to update notification preference");
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we're in sync with the server
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.USER_TECHNICAL_SUPPORT, backend.fetch] });
    },
  });

  const handleTechnicalSupportToggle = (event) => {
    const newValue = event.target.checked;
    updateTechnicalSupport(newValue);
  };

  return (
    <Container>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{ minHeight: "80vh", paddingTop: 20, paddingBottom: 20 }}
      >
        <Grid item xs={12} md={6}>
          <Box p={isMobile ? 2 : 4} boxShadow={3} borderRadius={2} textAlign="center" bgcolor="background.paper">
            <Typography variant="h4" gutterBottom>
              Profile Information
            </Typography>
            <List>
              <ListItem style={listItemStyle}>
                <ListItemText
                  primary={
                    <Typography variant="body1" style={typographyBold}>
                      Name
                    </Typography>
                  }
                  secondary={login.name}
                />
              </ListItem>
              <ListItem style={listItemStyle}>
                <ListItemText
                  primary={
                    <Typography variant="body1" style={typographyBold}>
                      Username
                    </Typography>
                  }
                  secondary={login.username}
                />
              </ListItem>
              <ListItem style={listItemStyle}>
                <ListItemText
                  primary={
                    <Typography variant="body1" style={typographyBold}>
                      Company
                    </Typography>
                  }
                  secondary={login.company_name}
                />
              </ListItem>
              <ListItem style={listItemStyle}>
                <Tooltip title="Change Password">
                  <IconButton
                    disabled={isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey())}
                    onClick={(event) => handlePopoverOpen(event, login, UserActions.EDIT_PROFILE_PASSWORD)}
                  >
                    <Lock />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Profile">
                  <IconButton
                    disabled={isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey())}
                    onClick={(event) => handlePopoverOpen(event, login, UserActions.EDIT_PROFILE_NAME)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              </ListItem>
              <ListItem style={listItemStyle}>
                <ListItemText
                  primary={
                    <Typography variant="body1" style={typographyBold}>
                      Role
                    </Typography>
                  }
                  secondary={login.roles ? login.roles.join(", ") : login.role}
                />
              </ListItem>
              <ListItem style={listItemStyle}>
                <ListItemText
                  primary={
                    <Typography variant="body1" style={typographyBold}>
                      Alert Email Notifications
                    </Typography>
                  }
                  secondary={
                    isTechnicalSupportLoading ? (
                      <Skeleton width={60} />
                    ) : technicalSupportEnabled ? (
                      "Enabled"
                    ) : (
                      "Disabled"
                    )
                  }
                />
                {isTechnicalSupportLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Switch
                    checked={technicalSupportEnabled}
                    onChange={handleTechnicalSupportToggle}
                    disabled={
                      isTechnicalSupportPending ||
                      isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey())
                    }
                    color="primary"
                  />
                )}
              </ListItem>
              <ListItem style={listItemStyle}>
                <ListItemText
                  primary={
                    <Typography variant="body1" style={typographyBold}>
                      Privileges
                    </Typography>
                  }
                />
              </ListItem>
              <PrivilegesList privileges={privileges} />
            </List>
            {popoverData.open ? (
              <UserPopover
                open={popoverData.open}
                anchorEl={popoverData.anchorEl}
                userRow={popoverData.userRow}
                actionType={popoverData.actionType}
                onClose={handlePopoverClose}
                partnerCompaniesPickList={popoverData.partnerCompaniesData}
                companiesError={popoverData.companiesError}
                userRolesPickList={popoverData.userRolesData}
                rolesError={popoverData.rolesError}
              />
            ) : null}
          </Box>
        </Grid>
      </Grid>

      {/* Dialog for last user in company */}
      <Dialog open={lastUserDialogOpen} onClose={() => setLastUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ mt: 2 }}>
          <strong>Alert Email Notifications</strong>
        </DialogTitle>
        <DialogContent>
          <Typography paragraph sx={{ mt: 1 }}>
            Wij stellen vast dat u momenteel het enige contactadres bent voor het ontvangen van de ALERTING-mails.
            Indien u deze meldingen wenst te deactiveren, dient u eerst een andere collega binnen uw organisatie aan te
            duiden die de ALERTING-mails zal ontvangen.
          </Typography>
          <Typography paragraph sx={{ mb: 1 }}>
            Wenst u hiervoor een afzonderlijk e-mailadres te gebruiken, dan kunt u via{" "}
            <strong>elionpartner@elindus.be</strong> een nieuw adres laten aanmaken dat specifiek voor deze meldingen
            wordt ingesteld.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLastUserDialogOpen(false)}
            variant="contained"
            color="primary"
            sx={{ mb: 2, mr: 2 }}
          >
            Begrepen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
